// src/hooks/useOrgInfo.ts
import { useEffect, useState } from 'react';
import { type OrgPalette, DEFAULT_PALETTE } from '@/context/BrandingContext';

// Theme colors from backend (.env per-VPS)
export interface ThemeColors {
  acc1: string;
  acc2: string;
  acc3: string;
  bgDark: string;
  bgLight: string;
  textDark: string;
  textLight: string;
}

export interface OrgInfoResponse {
  name: string;
  logo?: string | null;
  product?: string | null;
  theme?: ThemeColors | null;
}

// Default recruitment-friendly theme
const DEFAULT_THEME: ThemeColors = {
  acc1: '#00e5ff',
  acc2: '#7a5cff',
  acc3: '#ff4d8d',
  bgDark: '#0e1115',
  bgLight: '#f6f7fb',
  textDark: '#eaf0f7',
  textLight: '#131722',
};

export function useOrgInfo(apiBaseUrl: string, apiKey: string | null) {
  const [orgName, setOrgName] = useState('');
  const [orgLogo, setOrgLogo] = useState<string | null>(null);
  const [agentPhoto, setAgentPhoto] = useState<string | null>(null);
  const [palette, setPalette] = useState<OrgPalette>(DEFAULT_PALETTE);

  useEffect(() => {
    if (!apiKey) return;

    const fetchOrgInfo = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/org/info`, {
          headers: { 'x-api-key': apiKey },
        });
        if (!response.ok) throw new Error('No se pudo obtener info de la organización');
        const data: { name: string; logo?: string | null; agent_photo?: string | null; palette?: OrgPalette } = await response.json();
        setOrgName(data.name || 'Dashboard');
        setOrgLogo(data.logo || null);
        setAgentPhoto(data.agent_photo || null);
        if (data.palette) {
          setPalette({ ...DEFAULT_PALETTE, ...data.palette });
        }
      } catch (e) {
        console.warn('⚠️ Org info fallback');
        setOrgName('Dashboard');
        setOrgLogo(null);
        setAgentPhoto(null);
      }
    };

    fetchOrgInfo();
  }, [apiBaseUrl, apiKey]);

  return { orgName, orgLogo, agentPhoto, palette };
}
